import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useOrganization } from '@clerk/clerk-react';
import { Button, Card, Input, Select, Tabs, Textarea } from '@/components/ui';
import { SOCIAL_TONES, DEFAULT_SOCIAL_TONE } from '@/lib/socialTones';
import { Icon } from '@/lib/icons';
import { FaqPages } from '@/components/FaqPages';
import { useDictation, MicButton } from '@/lib/dictation';
import { useIsAdmin } from '@/auth/useIsAdmin';
import { GalleryManager } from '@/components/studio/GalleryManager';
import {
  getStudioStatus, getStudioFeedUrl, getStudioProjects, createStudioProject, patchStudioProject,
  discardStudioProjectIfEmpty,
  deleteStudioProject, submitStudioProject, studioLeadSearch, studioReviewSearch, studioSocialCaptions,
  studioSocialQueue, reviewStudioSocialPost,
  getFaqs, addFaq, updateFaq, deleteFaq, suggestFaqs, type FaqPair, type FaqRow, uploadContentDocument, getInterviewSettings,
  type StudioProject, type StudioProjectMeta, type StudioLeadHit, type StudioReviewHit,
} from '@/data/client';
import { InterviewChat } from '@/routes/ContentIQ';
import { CaseStudyPhotos, ProjectPhotos } from '@/components/studio/CaseStudyPhotos';
import { getProjectPhotos } from '@/data/client';

const SOCIAL_PLATFORMS = ['Facebook', 'Instagram', 'LinkedIn'] as const;

function errMsg(e: unknown, fallback: string): string { return e instanceof Error ? e.message : fallback; }

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

function fmtWhen(iso: string): string {
  const d = new Date((iso || '').replace(' ', 'T') + (iso?.endsWith('Z') ? '' : 'Z'));
  if (isNaN(d.getTime())) return '';
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} week${Math.floor(days / 7) === 1 ? '' : 's'} ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/* ---------------------------------------------------------- status meta */
const STATUS: Record<string, { cls: string; label: string }> = {
  draft: { cls: 'draft', label: 'Draft' },
  ai: { cls: 'ai', label: 'AI drafting' },
  input: { cls: 'input', label: 'Needs your input' },
  review: { cls: 'review', label: 'In Triton review' },
  approved: { cls: 'approved', label: 'Approved' },
  live: { cls: 'live', label: 'Published' },
};
function StatusPill({ s }: { s: string }) {
  const m = STATUS[s] || STATUS.draft;
  return <span className={`es-status es-status--${m.cls}`}><span className="dot" />{m.label}</span>;
}

const TYPE_META: Record<string, { icon: string; label: string }> = {
  article: { icon: 'file-text', label: 'Article' },
  social: { icon: 'megaphone', label: 'Social post' },
};
const KIND_LABEL: Record<string, string> = {
  case: 'Case study', knowledge: 'Knowledge session', blog: 'Blog',
};

const ARTICLE_KINDS = [
  { id: 'case', icon: 'award', name: 'Case Study', desc: 'A finished project told start to finish. We interview you, then write it as a client story.', req: 'Works best with a lead attached', vault: true },
  { id: 'knowledge', icon: 'message-square', name: 'Knowledge Session', desc: 'Share what you know about a topic. Our AI interviews you and turns it into an expert article.', vault: true },
  { id: 'blog', icon: 'file-text', name: 'Submit a Blog', desc: 'Already written something? Paste or upload it and we’ll polish, structure and publish it.', vault: false },
] as const;

const STEPS: Record<string, string[]> = {
  'article-case': ['Client info', 'Assets', 'AI interview', 'Review'],
  'article-knowledge': ['Topic', 'Assets', 'AI interview', 'Review'],
  'article-blog': ['Details', 'Your draft', 'Assets', 'Review'],
  social: ['Images', 'AI captions', 'Platforms', 'Review'],
};

/* ---------------------------------------------------------- root */
/* Starting points for "Who is this for?" — suggestions only; anything typed
   is kept as written. Spans the clients tritonIQ serves: residential and
   commercial trades, and small businesses that are not contractors at all. */
const AUDIENCE_SUGGESTIONS = [
  'Homeowners researching a problem',
  'Homeowners comparing options',
  'Property managers',
  'Commercial property owners',
  'Facility managers',
  'General contractors and builders',
  'Other contractors and trades',
  'Small business owners',
  'First-time customers',
  'Existing customers',
];

export function ExpertStudio() {
  const qc = useQueryClient();
  const { data: status } = useQuery({ queryKey: ['studioStatus'], queryFn: getStudioStatus });
  const { data: projData } = useQuery({ queryKey: ['studioProjects'], queryFn: getStudioProjects });
  const projects = projData?.projects ?? [];

  const [stage, setStage] = useState<'pick' | 'list' | 'kind' | 'faq' | 'faqGeneral' | 'flow' | 'galleries'>('pick');
  const [open, setOpen] = useState<StudioProject | null>(null);
  const [nonce, setNonce] = useState(0);
  const [err, setErr] = useState('');

  const create = useMutation({
    mutationFn: (input: { type: string; kind?: string }) => createStudioProject(input),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['studioProjects'] });
      setOpen(r.project); setNonce((n) => n + 1); setStage('flow'); setErr('');
    },
    onError: (e) => setErr(errMsg(e, 'Could not start the project.')),
  });

  if (status && !status.enabled) {
    return (
      <div className="dash-stack">
        <Card padding="lg"><h2>Expert Studio</h2><p className="dash-muted">This isn&rsquo;t enabled for your account yet — ask your Triton team to turn it on.</p></Card>
      </div>
    );
  }

  const start = (t: string) => {
    if (t === 'article') { setStage('kind'); return; }
    /* Photos, unlike everything else here, are not a project: an album item is
       live the moment it uploads, so there is nothing to draft or submit. The
       tile opens the album manager instead of starting a flow. */
    if (t === 'gallery') { setStage('galleries'); return; }
    /* FAQs are a living list, not a project with steps — no studio_projects row
       and nothing to submit, so it never reaches create.mutate. */
    if (t === 'faq') { setStage('faq'); return; }
    create.mutate({ type: t });
  };
  const openProject = (p: StudioProject) => { setOpen(p); setNonce((n) => n + 1); setStage('flow'); };
  const toList = () => { qc.invalidateQueries({ queryKey: ['studioProjects'] }); setOpen(null); setStage('list'); };

  return (
    <div className="dash-stack">
      {err && <Card padding="lg"><p className="vault-err"><Icon name="alert-circle" size={13} /> {err}</p></Card>}
      {stage === 'list' && (
        <>
          <SocialApprovalQueue />
          <ProjectList projects={projects} onNew={() => setStage('pick')} onOpen={openProject} />
          <FeedCard />
        </>
      )}
      {stage === 'pick' && (
        <TypePicker onPick={start} pending={create.isPending} onHistory={() => setStage('list')} count={projects.length} />
      )}
      {stage === 'galleries' && <GalleryManager onBack={() => setStage('pick')} />}
      {stage === 'kind' && <KindPicker onPick={(k) => create.mutate({ type: 'article', kind: k })} pending={create.isPending} onBack={() => setStage('pick')} />}
      {stage === 'faq' && <FaqPages onBack={() => setStage('pick')} onGeneral={() => setStage('faqGeneral')} />}
      {stage === 'faqGeneral' && <FaqStudio onBack={() => setStage('faq')} />}
      {stage === 'flow' && open && (
        <Flow key={`${open.id}-${nonce}`} project={open} onExit={toList} onList={toList} />
      )}
    </div>
  );
}

/* ---------------------------------------------------------- social feed card */
function FeedCard() {
  const { data } = useQuery({ queryKey: ['studioFeed'], queryFn: getStudioFeedUrl });
  const [copied, setCopied] = useState('');
  if (!data?.url) return null;
  return (
    <Card padding="lg">
      <h3 className="cq-card__title"><Icon name="megaphone" size={15} /> Social syndication feeds (RSS)</h3>
      <p className="cq-card__hint">Connect these to a social scheduler (Buffer, Zapier, Make, dlvr.it…). Each platform feed carries your published gallery photos plus approved social posts targeted at that platform, with that platform&rsquo;s caption. The main feed carries everything with the generic caption.</p>
      {[{ label: 'All (main)', url: data.url }, ...SOCIAL_PLATFORMS.map((p) => ({ label: p, url: `${data.url}/${p.toLowerCase()}` }))].map((f) => (
        <div className="studio-feed" key={f.label}>
          <span className="es-feedlabel">{f.label}</span>
          <code className="studio-feed__url">{f.url}</code>
          <Button size="sm" variant="outline" iconLeft={<Icon name={copied === f.label ? 'check' : 'download'} size={13} />}
            onClick={() => { navigator.clipboard.writeText(f.url); setCopied(f.label); setTimeout(() => setCopied(''), 1500); }}>
            {copied === f.label ? 'Copied' : 'Copy'}
          </Button>
        </div>
      ))}
    </Card>
  );
}

/* ---------------------------------------------------------- platform preview */
const PLATFORM_ICON: Record<string, string> = { Facebook: 'facebook', Instagram: 'instagram', LinkedIn: 'linkedin' };

function SocialPreview({ platform, bizName, caption, image }: { platform: string; bizName: string; caption: string; image?: ReactNode }) {
  const initial = (bizName || 'Y')[0].toUpperCase();
  const handle = (bizName || 'yourbusiness').toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 20) || 'yourbusiness';
  const cls = platform === 'Instagram' ? 'ig' : platform === 'LinkedIn' ? 'li' : 'fb';
  let body: ReactNode;
  if (platform === 'Instagram') {
    body = (
      <>
        <div className="es-prev__head">
          <span className="es-prev__ava es-prev__ava--ig">{initial}</span>
          <span><strong>{handle}</strong></span>
          <span className="es-prev__more"><Icon name="more-horizontal" size={16} /></span>
        </div>
        <div className="es-prev__img es-prev__img--sq">{image}</div>
        <div className="es-prev__acts es-prev__acts--ig">
          <span className="l"><Icon name="heart" size={20} /><Icon name="message-circle" size={20} /><Icon name="send" size={20} /></span>
          <span className="r"><Icon name="bookmark" size={20} /></span>
        </div>
        <p className="es-prev__cap"><strong>{handle}</strong> {caption}</p>
      </>
    );
  } else if (platform === 'LinkedIn') {
    body = (
      <>
        <div className="es-prev__head">
          <span className="es-prev__ava es-prev__ava--li">{initial}</span>
          <span><strong>{bizName}</strong><em>Just now · <Icon name="globe" size={10} /></em></span>
          <span className="es-prev__more"><Icon name="more-horizontal" size={16} /></span>
        </div>
        <p className="es-prev__cap">{caption}</p>
        <div className="es-prev__img">{image}</div>
        <div className="es-prev__acts es-prev__acts--row">
          <span><Icon name="thumbs-up" size={14} /> Like</span>
          <span><Icon name="message-circle" size={14} /> Comment</span>
          <span><Icon name="repeat" size={14} /> Repost</span>
          <span><Icon name="send" size={14} /> Send</span>
        </div>
      </>
    );
  } else {
    body = (
      <>
        <div className="es-prev__head">
          <span className="es-prev__ava es-prev__ava--fb">{initial}</span>
          <span><strong>{bizName}</strong><em>Just now · <Icon name="globe" size={10} /></em></span>
          <span className="es-prev__more"><Icon name="more-horizontal" size={16} /></span>
        </div>
        <p className="es-prev__cap">{caption}</p>
        <div className="es-prev__img">{image}</div>
        <div className="es-prev__acts es-prev__acts--row">
          <span><Icon name="thumbs-up" size={14} /> Like</span>
          <span><Icon name="message-circle" size={14} /> Comment</span>
          <span><Icon name="send" size={14} /> Share</span>
        </div>
      </>
    );
  }
  return (
    <div className="es-prevwrap">
      <span className={`es-prev__tag es-prev__tag--${cls}`}><Icon name={PLATFORM_ICON[platform] || 'megaphone'} size={13} /> {platform} preview</span>
      <div className={`es-prev es-prev--${cls}`}>{body}</div>
    </div>
  );
}

/* ---------------------------------------------------------- admin approval queue */
function SocialApprovalQueue() {
  const qc = useQueryClient();
  const isAdmin = useIsAdmin();
  const { organization } = useOrganization();
  const bizName = organization?.name || 'Your business';
  const { data } = useQuery({ queryKey: ['studioSocialQueue'], queryFn: studioSocialQueue, enabled: isAdmin });
  const review = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'reject' }) => reviewStudioSocialPost(id, action),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['studioSocialQueue'] }); qc.invalidateQueries({ queryKey: ['studioProjects'] }); },
  });
  const posts = data?.posts ?? [];
  if (!isAdmin || !posts.length) return null;
  return (
    <Card padding="lg" className="es-queue">
      <h3 className="cq-card__title"><Icon name="clock" size={15} /> Social posts awaiting Triton approval ({posts.length})</h3>
      <p className="cq-card__hint">Nothing reaches the syndication feeds until approved here. Reject hands the post back to the client to revise and resubmit.</p>
      {posts.map((p) => (
        <div className="es-queue__item" key={p.id}>
          <div className="es-queue__previews">
            {(p.platforms.length ? p.platforms : ['Facebook']).map((plat) => (
              <SocialPreview key={plat} platform={plat} bizName={bizName}
                caption={p.captions[plat] || p.caption}
                image={p.images[0] ? <img src={p.images[0]} alt="" /> : undefined} />
            ))}
          </div>
          <div className="es-queue__side">
            <span className="es-hint">Submitted {fmtWhen(p.createdAt)}{p.timing ? ` · ${p.timing}` : ''}</span>
            <div className="es-queue__acts">
              <Button variant="primary" size="sm" iconLeft={<Icon name="check" size={14} />} disabled={review.isPending}
                onClick={() => review.mutate({ id: p.id, action: 'approve' })}>Approve to feed</Button>
              <Button variant="ghost" size="sm" iconLeft={<Icon name="x" size={14} />} disabled={review.isPending}
                onClick={() => { if (confirm('Reject this post? The client can revise and resubmit.')) review.mutate({ id: p.id, action: 'reject' }); }}>Reject</Button>
            </div>
          </div>
        </div>
      ))}
    </Card>
  );
}

/* ---------------------------------------------------------- project list */
function ProjectList({ projects, onNew, onOpen }: { projects: StudioProject[]; onNew: () => void; onOpen: (p: StudioProject) => void }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [tab, setTab] = useState('all');
  /* A refused delete has to say so. Without onError the row simply stayed put and
     a real server message ("could not remove the photos from your website") went
     nowhere — indistinguishable from a dead button. */
  const [delErr, setDelErr] = useState('');
  const del = useMutation({
    mutationFn: (id: string) => deleteStudioProject(id),
    onSuccess: () => { setDelErr(''); qc.invalidateQueries({ queryKey: ['studioProjects'] }); },
    onError: (e) => setDelErr(errMsg(e, 'Could not delete that project.')),
  });
  const shown = projects.filter((p) =>
    (tab === 'all' || p.type === tab) && p.name.toLowerCase().includes(q.trim().toLowerCase()));
  const tabs = [
    { value: 'all', label: 'All', count: projects.length },
    { value: 'article', label: 'Articles', count: projects.filter((p) => p.type === 'article').length },
    { value: 'social', label: 'Social posts', count: projects.filter((p) => p.type === 'social').length },
  ];
  return (
    <div>
      <div className="es-head">
        <div className="es-head__copy">
          <h2>Past projects</h2>
          <p>Everything you&rsquo;ve created in Expert Studio. Open any project to keep working on it or check where it stands.</p>
        </div>
        <div className="es-head__actions">
          <Button variant="primary" iconLeft={<Icon name="plus" size={17} />} onClick={onNew}>New project</Button>
        </div>
      </div>

      <div className="es-filters">
        <div style={{ width: 260 }}>
          <Input placeholder="Search projects…" value={q} onChange={(e) => setQ(e.target.value)} leadingIcon={<Icon name="search" size={17} />} />
        </div>
        <span className="es-filters__count">{shown.length} of {projects.length}</span>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Tabs variant="pill" items={tabs} value={tab} onChange={setTab} />
      </div>

      {delErr && <p className="vault-err" style={{ margin: '0 0 12px' }}><Icon name="alert-circle" size={13} /> {delErr}</p>}
      {shown.length === 0 ? (
        <div className="dash-empty">
          <span className="dash-empty__icon"><Icon name="layers" size={26} /></span>
          <h2>No projects yet</h2>
          <p>Start your first project and it will show up here.</p>
          <div style={{ marginTop: 16 }}>
            <Button variant="primary" iconLeft={<Icon name="plus" size={17} />} onClick={onNew}>New project</Button>
          </div>
        </div>
      ) : (
        <div className="es-list">
          {shown.map((p) => {
            const m = TYPE_META[p.type] || TYPE_META.article;
            return (
              <div className="es-row" key={p.id} onClick={() => onOpen(p)}>
                <span className="es-row__ic"><Icon name={m.icon} size={19} /></span>
                <div className="es-row__main">
                  <strong>{p.name}</strong>
                  <span className="es-row__meta">
                    {KIND_LABEL[p.kind] || m.label}
                    {p.published?.title && p.published.title !== p.name && <> · published as &ldquo;{p.published.title}&rdquo;</>}
                    {p.published?.url && <> · <a href={p.published.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>view live</a></>}
                  </span>
                </div>
                <span className="es-row__type">{m.label}</span>
                <StatusPill s={p.status} />
                <span className="es-row__when">{fmtWhen(p.updatedAt)}</span>
                {p.submitted ? <span /> : (
                  <button type="button" className="es-row__del"
                    title={p.status === 'live' ? 'Remove from website' : 'Delete draft'}
                    onClick={(e) => {
                      e.stopPropagation();
                      const msg = `Delete "${p.name}"? This removes the draft and its uploads.`;
                      if (confirm(msg)) del.mutate(p.id);
                    }}>
                    <Icon name="trash" size={14} />
                  </button>
                )}
                <span className="es-row__chev"><Icon name="chevron-right" size={17} /></span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------- FAQs */

/* The client answers their own customers' questions, straight into the Vault.

   Not a step flow like the other project types: an FAQ list is a living thing
   you come back to, not something submitted once.

   Answered ones collapse to a single line at the BOTTOM and unanswered drafts
   sit expanded at the top, because a hundred fully-expanded answers is a page
   nobody can work in. A stored FAQ always has both halves — half a Q&A pair in
   the vault would have generation read a question nobody answered — so anything
   still waiting for an answer is a local draft, not a row. */
function FaqStudio({ onBack }: { onBack: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['faqs'], queryFn: getFaqs });
  const saved = data?.faqs ?? [];

  /* Drafts live only here until they have an answer worth storing. */
  const [drafts, setDrafts] = useState<FaqPair[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [suggested, setSuggested] = useState<string[]>([]);
  const [editing, setEditing] = useState<string | null>(null);

  const apply = (faqs: FaqRow[]) => qc.setQueryData(['faqs'], { faqs });

  const addDraft = (question = '') => setDrafts((d) => [...d, { question, answer: '' }]);
  const setDraft = (i: number, patch: Partial<FaqPair>) =>
    setDrafts((d) => d.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const commit = async (i: number) => {
    const d = drafts[i];
    if (!d.question.trim() || !d.answer.trim()) { setErr('Add both a question and an answer.'); return; }
    setBusy(true); setErr('');
    try {
      apply((await addFaq(d)).faqs);
      setDrafts((prev) => prev.filter((_, j) => j !== i));
    } catch (e) { setErr(errMsg(e, 'Could not save that FAQ.')); }
    finally { setBusy(false); }
  };

  const saveEdit = async (id: string, pair: FaqPair) => {
    setBusy(true); setErr('');
    try { apply((await updateFaq(id, pair)).faqs); setEditing(null); }
    catch (e) { setErr(errMsg(e, 'Could not save that change.')); }
    finally { setBusy(false); }
  };

  const remove = async (id: string) => {
    setBusy(true); setErr('');
    try { apply((await deleteFaq(id)).faqs); }
    catch (e) { setErr(errMsg(e, 'Could not delete that FAQ.')); }
    finally { setBusy(false); }
  };

  const suggest = async () => {
    setSuggesting(true); setErr('');
    try {
      const r = await suggestFaqs();
      const known = [...saved, ...drafts].map((f) => f.question.trim().toLowerCase());
      /* Questions only, never answers — the whole value is that the words are
         the client's own. */
      setSuggested(r.questions.filter((q) => !known.includes(q.trim().toLowerCase())));
    } catch (e) { setErr(errMsg(e, 'Could not suggest questions.')); }
    finally { setSuggesting(false); }
  };

  return (
    <div>
      <div className="es-head">
        <div className="es-head__copy">
          <h2>Frequently asked questions</h2>
          <p>
            The questions your customers actually ask, answered in your own words. Each one goes into your
            Authenticity Vault, so everything we write for you &mdash; pages, blogs, case studies &mdash; can draw on it.
          </p>
        </div>
        <div className="es-head__actions">
          <Button variant="outline" iconLeft={<Icon name="arrow-left" size={16} />} onClick={onBack}>Back</Button>
        </div>
      </div>

      {isLoading ? <div className="lead-none">Loading…</div> : (
        <>
          {drafts.length > 0 && (
            <>
              <span className="es-label">Needs an answer</span>
              <div className="es-faqs">
                {drafts.map((d, i) => (
                  <FaqDraft key={i} value={d} busy={busy}
                    onChange={(patch) => setDraft(i, patch)}
                    onSave={() => commit(i)}
                    onDiscard={() => setDrafts((prev) => prev.filter((_, j) => j !== i))} />
                ))}
              </div>
            </>
          )}

          <div className="es-drop" onClick={() => addDraft()} style={{ marginTop: drafts.length ? 12 : 0 }}>
            <span className="es-drop__ic"><Icon name="plus" size={20} /></span>
            <strong>Add a question</strong>
            <span>Something a customer asks, and how you answer it</span>
          </div>

          {err && <p className="vault-err" style={{ marginTop: 10 }}><Icon name="alert-circle" size={13} /> {err}</p>}

          <div className="vault-add__actions" style={{ marginTop: 14 }}>
            <Button variant="outline" disabled={suggesting} onClick={suggest}
              iconLeft={<Icon name={suggesting ? 'refresh-cw' : 'sparkles'} size={15} />}>
              {suggesting ? 'Thinking…' : 'Suggest questions'}
            </Button>
          </div>

          {suggested.length > 0 && (
            <div className="es-faqsug">
              <span className="es-label">Questions worth answering</span>
              <p className="es-hint">Suggested from your trade and what your Vault doesn&rsquo;t cover yet. Add one and answer it in your own words.</p>
              {suggested.map((q) => (
                <button type="button" key={q} className="cov-alt" onClick={() => { addDraft(q); setSuggested((p) => p.filter((x) => x !== q)); }}>
                  <span>{q}</span><Icon name="plus" size={13} />
                </button>
              ))}
            </div>
          )}

          {saved.length > 0 && (
            <div className="es-faqdone">
              <span className="es-label">Answered · {saved.length}</span>
              <div className="es-faqs">
                {saved.map((f) => (
                  <FaqSaved key={f.id} faq={f} busy={busy}
                    open={editing === f.id}
                    onOpen={() => setEditing(editing === f.id ? null : f.id)}
                    onSave={(pair) => saveEdit(f.id, pair)}
                    onRemove={() => remove(f.id)} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** A draft: expanded, with a mic, until it has an answer worth storing. */
function FaqDraft({ value, busy, onChange, onSave, onDiscard }: {
  value: FaqPair; busy: boolean;
  onChange: (patch: Partial<FaqPair>) => void; onSave: () => void; onDiscard: () => void;
}) {
  // Same dictation the AI interview uses — a contractor will speak three good
  // answers before typing one.
  const dictation = useDictation((next) => onChange({ answer: next }));
  return (
    <div className="es-faq">
      <Input placeholder="What do customers ask?" value={value.question}
        onChange={(e) => onChange({ question: e.target.value })} />
      <Textarea rows={3} value={value.answer} style={{ marginTop: 8 }}
        placeholder={dictation.listening ? 'Listening…' : 'Your answer, in your own words…'}
        onChange={(e) => onChange({ answer: e.target.value })} />
      {dictation.micError && <p className="vault-err"><Icon name="alert-circle" size={13} /> {dictation.micError}</p>}
      <div className="es-faq__foot">
        <MicButton listening={dictation.listening}
          onToggle={() => (dictation.listening ? dictation.stop() : dictation.start(value.answer))} />
        <Button size="sm" variant="ghost" disabled={busy} onClick={onDiscard}>Discard</Button>
        <Button size="sm" variant="primary" disabled={busy || !value.question.trim() || !value.answer.trim()} onClick={onSave}>
          Save to Vault
        </Button>
      </div>
    </div>
  );
}

/** A stored FAQ: the question only, until the pencil opens it. */
function FaqSaved({ faq, busy, open, onOpen, onSave, onRemove }: {
  faq: FaqRow; busy: boolean; open: boolean;
  onOpen: () => void; onSave: (pair: FaqPair) => void; onRemove: () => void;
}) {
  const [q, setQ] = useState(faq.question);
  const [a, setA] = useState(faq.answer);
  const dictation = useDictation(setA);
  // Reset the boxes whenever it is reopened, so a cancelled edit is really gone.
  useEffect(() => { if (open) { setQ(faq.question); setA(faq.answer); } }, [open, faq.question, faq.answer]);

  if (!open) {
    return (
      <div className="es-faq es-faq--row">
        <button type="button" className="es-faq__line" onClick={onOpen} title="Edit this answer">
          <Icon name="quote" size={13} />
          <span>{faq.question}</span>
        </button>
        <div className="es-faq__ord">
          <button type="button" className="es-row__del" title="Edit" onClick={onOpen}><Icon name="edit" size={14} /></button>
          <button type="button" className="es-row__del" title="Delete" disabled={busy}
            onClick={() => { if (confirm(`Delete “${faq.question}”?`)) onRemove(); }}>
            <Icon name="trash" size={14} />
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="es-faq">
      <Input value={q} onChange={(e) => setQ(e.target.value)} />
      <Textarea rows={4} value={a} style={{ marginTop: 8 }}
        placeholder={dictation.listening ? 'Listening…' : 'Your answer…'}
        onChange={(e) => setA(e.target.value)} />
      {dictation.micError && <p className="vault-err"><Icon name="alert-circle" size={13} /> {dictation.micError}</p>}
      <div className="es-faq__foot">
        <MicButton listening={dictation.listening}
          onToggle={() => (dictation.listening ? dictation.stop() : dictation.start(a))} />
        <Button size="sm" variant="ghost" disabled={busy} onClick={onOpen}>Cancel</Button>
        <Button size="sm" variant="primary" disabled={busy || !q.trim() || !a.trim()} onClick={() => onSave({ question: q, answer: a })}>
          Save
        </Button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------- type picker */
function TypePicker({ onPick, onHistory, count, pending }: {
  onPick: (t: string) => void; onHistory: () => void; count: number; pending: boolean;
}) {
  return (
    <div>
      <div className="es-head">
        <div className="es-head__copy">
          <h2>Create a project</h2>
          <p>Pick what you want to make. We&rsquo;ll walk you through it step by step — you bring the expertise, we handle the writing and formatting.</p>
        </div>
        <div className="es-head__actions">
          <Button variant="outline" iconLeft={<Icon name="layers" size={16} />} onClick={onHistory}>Past projects{count ? ` (${count})` : ''}</Button>
        </div>
      </div>
      <div className="es-picker">
        <button type="button" className="es-pick" disabled={pending} onClick={() => onPick('article')}>
          <span className="es-pick__ic"><Icon name="file-text" size={24} /></span>
          <h3>Content / Article</h3>
          <p>Turn your knowledge and finished projects into written content for your site.</p>
          <ul className="es-pick__list">
            <li><Icon name="check" size={13} /> Case study — guided AI interview</li>
            <li><Icon name="check" size={13} /> Knowledge session</li>
            <li><Icon name="check" size={13} /> Submit your own blog</li>
          </ul>
          <span className="es-pick__go">Start <Icon name="arrow-right" size={15} /></span>
        </button>
        <button type="button" className="es-pick" disabled={pending} onClick={() => onPick('gallery')}>
          <span className="es-pick__ic"><Icon name="image" size={24} /></span>
          <h3>Image Gallery</h3>
          <p>Your project photos, organised into albums. Everything you add is live on your site straight away.</p>
          <ul className="es-pick__list">
            <li><Icon name="check" size={13} /> Albums &mdash; group photos however you like</li>
            <li><Icon name="check" size={13} /> Before &amp; after &mdash; paired transformations</li>
            <li><Icon name="check" size={13} /> Reuse in social posts &amp; case studies</li>
          </ul>
          <span className="es-pick__go">Open <Icon name="arrow-right" size={15} /></span>
        </button>
        <button type="button" className="es-pick" disabled={pending} onClick={() => onPick('faq')}>
          <span className="es-pick__ic"><Icon name="quote" size={24} /></span>
          <h3>FAQs</h3>
          <p>Answer the questions your customers actually ask, in your own words.</p>
          <ul className="es-pick__list">
            <li><Icon name="check" size={13} /> We suggest questions worth answering</li>
            <li><Icon name="check" size={13} /> Speak your answers if it&rsquo;s easier</li>
            <li><Icon name="check" size={13} /> Feeds everything we write for you</li>
          </ul>
          <span className="es-pick__go">Start <Icon name="arrow-right" size={15} /></span>
        </button>
        <button type="button" className="es-pick" disabled={pending} onClick={() => onPick('social')}>
          <span className="es-pick__ic"><Icon name="megaphone" size={24} /></span>
          <h3>Social Post</h3>
          <p>Upload images and let our AI write captions tuned to each platform.</p>
          <ul className="es-pick__list">
            <li><Icon name="check" size={13} /> AI scans your images</li>
            <li><Icon name="check" size={13} /> Captions per platform</li>
            <li><Icon name="check" size={13} /> Saves to your syndication feed</li>
          </ul>
          <span className="es-pick__go">Start <Icon name="arrow-right" size={15} /></span>
        </button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------- article kind picker */
function KindPicker({ onPick, onBack, pending }: { onPick: (k: string) => void; onBack: () => void; pending: boolean }) {
  return (
    <div>
      <div className="es-head">
        <div className="es-head__copy">
          <h2>What kind of article?</h2>
          <p>Each one starts differently, but we do the writing either way.</p>
        </div>
        <div className="es-head__actions">
          <Button variant="outline" iconLeft={<Icon name="arrow-left" size={16} />} onClick={onBack}>Back</Button>
        </div>
      </div>
      <div className="es-subs">
        {ARTICLE_KINDS.map((k) => (
          <button type="button" className="es-sub" key={k.id} disabled={pending} onClick={() => onPick(k.id)}>
            <span className="es-sub__ic"><Icon name={k.icon} size={20} /></span>
            <strong>{k.name}</strong>
            <p>{k.desc}</p>
            {'req' in k && k.req && <span className="es-sub__req"><Icon name="alert-circle" size={11} /> {k.req}</span>}
            {k.vault
              ? <span className="es-sub__vault"><Icon name="shield-check" size={11} /> Interview &amp; documents are saved to your Authenticity Vault as grounded data</span>
              : <span className="es-sub__vault es-sub__vault--off"><Icon name="info" size={11} /> Becomes a writing topic — grounded by your Vault, but your draft isn&rsquo;t stored as Vault data</span>}
          </button>
        ))}
      </div>
      <p className="es-vaultnote">
        <Icon name="shield-check" size={13} />
        <span>The Authenticity Vault only holds real, human material — your words, your documents. <strong>Please don&rsquo;t submit AI-generated content</strong>; it would contaminate the grounding your articles are built on.</span>
      </p>
    </div>
  );
}

/* Mirrors KindPicker: the gallery type is fixed at creation because it decides
   the step flow, the uploader, and which block the page publishes through. */
function LeadSearch({ picked, onPick, onClear }: { picked: StudioProjectMeta['lead']; onPick: (l: StudioLeadHit) => void; onClear: () => void }) {
  const [q, setQ] = useState('');
  const dq = useDebounced(q.trim(), 300);
  const { data, isFetching } = useQuery({ queryKey: ['studioLeadSearch', dq], queryFn: () => studioLeadSearch(dq), enabled: dq.length > 1 });

  if (picked) {
    return (
      <div className="es-attached">
        <div className="es-attached__top">
          <Icon name="users" size={14} />
          <strong>Lead attached</strong>
          <button type="button" className="x" onClick={onClear} title="Remove"><Icon name="x" size={14} /></button>
        </div>
        <div className="es-attached__who">
          <b>{picked.name}</b>
          <span>{[picked.city && picked.state ? `${picked.city}, ${picked.state}` : picked.city || picked.state].filter(Boolean).join('')}</span>
        </div>
        {picked.request && <div className="es-quote"><span className="k">Original request</span>{picked.request}</div>}
      </div>
    );
  }
  const hits = data?.leads ?? [];
  return (
    <div className="es-search">
      <div className="es-search__bar">
        <span className="lbl"><Icon name="search" size={13} /> Find the lead</span>
        <span className="grow"><Input placeholder="Search leads by name or city…" value={q} onChange={(e) => setQ(e.target.value)} /></span>
      </div>
      {dq.length <= 1 ? (
        <div className="es-search__empty">Start typing to search your stored leads.</div>
      ) : isFetching ? (
        <div className="es-search__empty">Searching…</div>
      ) : hits.length === 0 ? (
        <div className="es-search__empty">No leads match &ldquo;{dq}&rdquo;.</div>
      ) : (
        <div className="es-results">
          {hits.map((x) => (
            <div className="es-result" key={x.id} onClick={() => onPick(x)}>
              <div className="es-result__main">
                <strong>{x.name}</strong>
                <span>{[x.city && x.state ? `${x.city}, ${x.state}` : x.city || x.state, x.channel].filter(Boolean).join(' · ')}</span>
              </div>
              <span className="es-result__stars">{x.score != null ? `${x.score}★` : ''}</span>
              <span className="es-result__when">{x.when}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewSearch({ picked, onPick, onClear }: { picked: StudioProjectMeta['review']; onPick: (r: StudioReviewHit) => void; onClear: () => void }) {
  const [q, setQ] = useState('');
  const dq = useDebounced(q.trim(), 300);
  const { data, isFetching } = useQuery({ queryKey: ['studioReviewSearch', dq], queryFn: () => studioReviewSearch(dq), enabled: dq.length > 1 });

  if (picked) {
    return (
      <div className="es-attached">
        <div className="es-attached__top">
          <Icon name="star" size={14} />
          <strong>Review attached</strong>
          <button type="button" className="x" onClick={onClear} title="Remove"><Icon name="x" size={14} /></button>
        </div>
        <div className="es-attached__who">
          <b>{picked.author}</b>
          <span>{picked.date}</span>
          <span className="es-result__stars">{'★'.repeat(Math.max(1, Math.min(5, picked.rating)))}</span>
        </div>
        <div className="es-quote"><span className="k">What they wrote</span>{picked.content}</div>
      </div>
    );
  }
  const hits = data?.reviews ?? [];
  return (
    <div className="es-search">
      <div className="es-search__bar">
        <span className="lbl"><Icon name="search" size={13} /> Find a review</span>
        <span className="grow"><Input placeholder="Search reviews by name…" value={q} onChange={(e) => setQ(e.target.value)} /></span>
      </div>
      {dq.length <= 1 ? (
        <div className="es-search__empty">Start typing to search your reviews.</div>
      ) : isFetching ? (
        <div className="es-search__empty">Searching…</div>
      ) : hits.length === 0 ? (
        <div className="es-search__empty">No reviews match &ldquo;{dq}&rdquo;.</div>
      ) : (
        <div className="es-results">
          {hits.map((x, i) => (
            <div className="es-result" key={`${x.author}-${i}`} onClick={() => onPick(x)}>
              <div className="es-result__main">
                <strong>{x.author}</strong>
                <span>{x.content.slice(0, 90)}{x.content.length > 90 ? '…' : ''}</span>
              </div>
              <span className="es-result__stars">{'★'.repeat(Math.max(1, Math.min(5, x.rating)))}</span>
              <span className="es-result__when">{x.date}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------- uploads */
/* Document uploads bound to an article. */
function ArticleDocDrop({ articleId, hint }: { articleId: string; hint?: string }) {
  const [names, setNames] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [over, setOver] = useState(false);
  const inRef = useRef<HTMLInputElement>(null);
  const onFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true); setErr('');
    try { for (const f of Array.from(files)) { await uploadContentDocument(articleId, f); setNames((p) => [...p, f.name]); } }
    catch (e) { setErr(errMsg(e, 'Upload failed.')); }
    finally { setBusy(false); if (inRef.current) inRef.current.value = ''; }
  };
  return (
    <>
      <div className={`es-drop${over ? ' es-drop--over' : ''}`} onClick={() => inRef.current?.click()}
        onDragEnter={(e) => { e.preventDefault(); setOver(true); }}
        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; if (!over) setOver(true); }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false); }}
        onDrop={(e) => { e.preventDefault(); setOver(false); void onFiles(e.dataTransfer.files); }}>
        <span className="es-drop__ic"><Icon name="file-text" size={20} /></span>
        <strong>{busy ? 'Uploading…' : over ? 'Drop to add' : 'Click or drag documents here'}</strong>
        <span>{hint || 'PDF, DOC, DOCX or TXT'}</span>
        <input ref={inRef} type="file" accept=".pdf,.doc,.docx,.txt,.md" multiple hidden onChange={(e) => onFiles(e.target.files)} />
      </div>
      {err && <p className="vault-err" style={{ marginTop: 8 }}><Icon name="alert-circle" size={13} /> {err}</p>}
      {names.length > 0 && (
        <div className="es-docs">
          {names.map((n, i) => (
            <div className="es-doc" key={i}>
              <span className="es-doc__ic"><Icon name="file-text" size={16} /></span>
              <span className="es-doc__main"><strong>{n}</strong><span>uploaded</span></span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
function Done({ project, published, onList }: { project: StudioProject; published: boolean; onList: () => void }) {
  return (
    <div className="es-done">
      <span className="es-done__ic"><Icon name={published ? 'check-circle' : 'send'} size={30} /></span>
      <h3>{published ? 'Published to your site' : 'Sent to Triton'}</h3>
      <p>
        {published
          ? 'It’s live now. You can keep editing from your project list at any time.'
          : 'Our team will polish and publish this, usually within two business days. You’ll see the status update in your project list.'}
        {project.type === 'social' && ' Once approved, it appears in the syndication feeds for the platforms you picked.'}
      </p>
      <div className="es-done__next">
        <Button variant="primary" onClick={onList}>Back to projects</Button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------- guided flow */
function Flow({ project, onExit, onList }: {
  project: StudioProject;
  onExit: () => void;
  onList: () => void;
}) {
  const qc = useQueryClient();
  const flowKey = project.type === 'article' ? `article-${project.kind}` : project.type;
  const steps = STEPS[flowKey] || STEPS.social;
  /* Published (or pushed to the site, which reads the same here) means every
     step is done — the flow opens on the last step with the strip complete,
     instead of on whatever step the client last touched. */
  const isLive = project.status === 'live';
  const [i, setI] = useState(isLive ? steps.length - 1 : Math.min(project.step, steps.length - 1));
  const DEFAULT_NAMES = ['New case study', 'New knowledge session', 'New blog submission', 'New social post'];
  const [name, setName] = useState(DEFAULT_NAMES.includes(project.name) ? '' : project.name);
  const [meta, setMeta] = useState<StudioProjectMeta>(project.meta || {});
  const [interviewOpen, setInterviewOpen] = useState(false);
  const [interviewDone, setInterviewDone] = useState(['review', 'approved', 'live', 'ai'].includes(project.status));
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState(true);
  const { data: settings } = useQuery({ queryKey: ['interview', 'settings'], queryFn: getInterviewSettings });

  const kindName = project.kind ? KIND_LABEL[project.kind] : (TYPE_META[project.type] || TYPE_META.article).label;
  const fields = meta.fields || {};
  const setField = (k: string, v: string) => { setMeta((m) => ({ ...m, fields: { ...(m.fields || {}), [k]: v } })); setSaved(false); };

  const save = useMutation({
    mutationFn: async (patch: { step?: number }) => {
      await patchStudioProject(project.id, {
        name: name.trim() || undefined, meta, step: patch.step ?? i,
      });
    },
    onSuccess: () => { setSaved(true); setErr(''); },
    onError: (e) => setErr(errMsg(e, 'Could not save.')),
  });
  const goTo = (n: number) => { setI(n); save.mutate({ step: n }); };
  /* Exit with nothing entered deletes the project rather than autosaving an
     empty one: a picked kind is not a project until the client gives it
     something. The Worker makes the final call, so a photo uploaded on
     another step or an interview already started always keeps the project. */
  const pristine = i === 0 && !project.submitted && !name.trim() && !meta.fields && !meta.draftText && !meta.lead && !meta.review;
  const saveAndClose = () => {
    if (pristine) { void discardStudioProjectIfEmpty(project.id).finally(onExit); return; }
    save.mutate({}, { onSuccess: () => onExit() });
  };

  const submit = useMutation({
    mutationFn: async () => {
      await patchStudioProject(project.id, { name: name.trim() || undefined, meta, step: i });
      return submitStudioProject(project.id);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['studioProjects'] }); setDone(true); },
    onError: (e) => setErr(errMsg(e, 'Could not submit.')),
  });

  /* How many photos this post has — what the step-completion strip means by
     "images attached?". */
  const { data: socialPhotos } = useQuery({
    queryKey: ['projectPhotos', project.id],
    queryFn: () => getProjectPhotos(project.id),
    enabled: project.type === 'social',
  });
  const socialPhotoCount = socialPhotos?.photos.length ?? 0;

  // AI captions (social step 1) — one optimized caption per platform, all editable.
  const captions = useMutation({
    mutationFn: () => studioSocialCaptions({
      projectId: project.id,
      description: fields.description || '',
      tone: fields.tone || DEFAULT_SOCIAL_TONE,
    }),
    onSuccess: (r) => {
      const byPlatform: Record<string, string> = {};
      for (const cc of r.captions) byPlatform[cc.platform] = cc.text;
      setMeta((m) => ({ ...m, vision: r.vision, captions: byPlatform, caption: byPlatform.Facebook || r.captions[0]?.text || '' }));
      setSaved(false);
    },
    onError: (e) => setErr(errMsg(e, 'Caption generation failed.')),
  });
  const setPlatformCaption = (platform: string, text: string) => {
    setMeta((m) => {
      const caps = { ...(m.captions || {}), [platform]: text };
      return { ...m, captions: caps, caption: caps.Facebook || Object.values(caps)[0] || '' };
    });
    setSaved(false);
  };
  const hasCaptions = Object.values(meta.captions || {}).some((t) => (t || '').trim());
  const { organization } = useOrganization();
  const bizName = organization?.name || 'Your business';
  /* The first photo, as the social preview's image. */
  const previewImgUrl = socialPhotos?.photos.find((ph) => ph.cdnUrl)?.cdnUrl || '';
  const previewImgNode = previewImgUrl ? <img src={previewImgUrl} alt="" /> : undefined;

  const style = settings?.style || 'guided';
  const last = i === steps.length - 1;

  const previewBody = useMemo(() => {
    if (project.type === 'social') return meta.caption || 'Pick or write a caption in the AI captions step.';
    if (project.kind === 'blog') return (meta.draftText || '').slice(0, 320) || 'Your submitted draft lands in our writing queue — we polish, structure and publish it.';
    return meta.lead?.request
      ? `Built from the customer’s original request: “${meta.lead.request.slice(0, 220)}…”`
      : 'Our writers turn your interview into the finished piece before anything goes live.';
  }, [project.type, project.kind, meta.caption, meta.draftText, meta.lead?.request]);

  const checks: { ok: boolean; label: string }[] = useMemo(() => {
    if (project.type === 'social') return [
      { ok: socialPhotoCount > 0, label: 'Images attached' },
      { ok: hasCaptions || !!meta.caption, label: 'Captions written' },
      { ok: (meta.platforms || []).length > 0, label: 'Platforms selected' },
    ];
    if (project.kind === 'blog') return [
      { ok: !!name.trim(), label: 'Title set' },
      { ok: !!(meta.draftText || '').trim(), label: 'Draft provided' },
    ];
    return [
      { ok: !!name.trim(), label: 'Details captured' },
      { ok: interviewDone, label: 'Your expertise recorded' },
      { ok: true, label: 'Assets attached (optional)' },
    ];
  }, [project.type, project.kind, name, socialPhotoCount, meta.caption, hasCaptions, meta.platforms, meta.draftText, interviewDone]);

  if (done) {
    return (
      <div className="es-flow">
        <div className="es-flow__bar"><span className="es-flow__name">{kindName}</span></div>
        <div className="es-flow__body">
          <Done project={project} published={false} onList={onList} />
        </div>
      </div>
    );
  }

  if (interviewOpen && project.articleId) {
    return (
      <div className="es-flow">
        <div className="es-flow__bar">
          <button type="button" className="es-flow__back" onClick={() => setInterviewOpen(false)}><Icon name="arrow-left" size={15} /> Back to steps</button>
          <span className="es-flow__name">{kindName}{name ? ` — ${name}` : ''}</span>
        </div>
        <div className="es-flow__body">
          {/* Closing the chat mid-interview just returns to the steps; it used to
              mark the interview done. Finishing submits the project on the spot —
              the review page that followed had exactly one button, "Send to
              Triton", so it was a stop with no decision on it — and the success
              screen leads back to the Studio home to start something else. */}
          <InterviewChat articleId={project.articleId} style={style}
            backLabel="Back to steps"
            onBack={() => setInterviewOpen(false)}
            onFinished={async () => {
              await patchStudioProject(project.id, { name: name.trim() || undefined, meta, step: steps.length - 1 });
              await submitStudioProject(project.id);
              setInterviewDone(true);
              qc.invalidateQueries({ queryKey: ['studioProjects'] });
            }}
            doneNote={(title) => `“${title}” has been sent to Triton for review. We’ll turn it into the finished piece and let you know when it’s ready. Thanks for the interview!`}
            doneLabel="Back to Expert Studio"
            onDoneBack={onList} />
        </div>
      </div>
    );
  }

  const togglePlat = (p: string) => {
    setMeta((m) => {
      const cur = m.platforms || [];
      return { ...m, platforms: cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p] };
    });
    setSaved(false);
  };

  return (
    <div className="es-flow">
      <div className="es-flow__bar">
        <button type="button" className="es-flow__back" onClick={saveAndClose}><Icon name="arrow-left" size={15} /> Exit</button>
        <span className="es-flow__name">{kindName}{name ? ` — ${name}` : ''}</span>
        <div className="es-flow__save">
          <span className="es-flow__autosave">
            <Icon name={saved ? 'check-circle' : 'clock'} size={13} /> {save.isPending ? 'Saving…' : saved ? 'Saved as draft' : 'Unsaved changes'}
          </span>
        </div>
      </div>

      <div className="es-steps">
        {steps.map((s, n) => (
          <span key={s} style={{ display: 'contents' }}>
            <div className={`es-step${n === i ? ' is-on' : ''}${n < i || isLive ? ' is-done' : ''}`}
              onClick={() => { if (n < i) goTo(n); }} style={n < i ? { cursor: 'pointer' } : undefined}>
              <span className="es-step__n">{n < i || isLive ? <Icon name="check" size={14} /> : n + 1}</span>
              <span className="es-step__lbl">{s}</span>
            </div>
            {n < steps.length - 1 && <span className={`es-steps__rail${n < i || isLive ? ' is-done' : ''}`} />}
          </span>
        ))}
      </div>

      <div className="es-flow__body">
        {err && <p className="vault-err" style={{ marginBottom: 14 }}><Icon name="alert-circle" size={13} /> {err}</p>}

        {/* ---------- article: case study ---------- */}
        {flowKey === 'article-case' && i === 0 && (
          <>
            <div className="es-section-note">
              <Icon name="lock" size={16} />
              <span>Search your stored leads to pull in the original request and autofill the details — or fill them in by hand. Client details stay private; our writing anonymizes contact info.</span>
            </div>
            <LeadSearch picked={meta.lead}
              onPick={(l) => {
                setMeta((m) => ({ ...m, lead: { uid: l.id, name: l.name, city: l.city, state: l.state, request: l.request } }));
                setSaved(false);
                if (!name.trim() && (l.city || l.name)) setName([l.city, fields.service].filter(Boolean).join(' ') || l.name);
              }}
              onClear={() => { setMeta((m) => ({ ...m, lead: undefined })); setSaved(false); }} />
            <div className="es-fields--two">
              <div><span className="es-label">Project name</span><Input placeholder="e.g. Edina hail replacement" value={name} onChange={(e) => { setName(e.target.value); setSaved(false); }} /></div>
              <div><span className="es-label">Service performed</span><Input placeholder="e.g. Roof replacement" value={fields.service || ''} onChange={(e) => setField('service', e.target.value)} /></div>
              <div><span className="es-label">Customer name</span><Input placeholder="First name is fine" value={fields.customerName ?? meta.lead?.name ?? ''} onChange={(e) => setField('customerName', e.target.value)} /></div>
              <div><span className="es-label">City / neighborhood</span><Input placeholder="e.g. Edina, MN" value={fields.city ?? (meta.lead ? [meta.lead.city, meta.lead.state].filter(Boolean).join(', ') : '')} onChange={(e) => setField('city', e.target.value)} /></div>
              <div><span className="es-label">Completion date</span><Input type="date" value={fields.completionDate || ''} onChange={(e) => setField('completionDate', e.target.value)} /></div>
              <div><span className="es-label">Permission to use their name?</span>
                <Select placeholder="Select" options={['Yes — full name OK', 'First name only', 'Keep anonymous']}
                  value={fields.permission || ''} onChange={(e) => { setField('permission', e.target.value); setMeta((m) => m.lead ? { ...m, lead: { ...m.lead, permission: e.target.value } } : m); }} />
              </div>
            </div>
            <div style={{ marginTop: 26 }}>
              <span className="es-label">Add a review to the story <span style={{ color: 'var(--text-subtle)', fontWeight: 400 }}>(optional)</span></span>
              <span className="es-hint" style={{ marginBottom: 12 }}>A customer&rsquo;s own words are the strongest close a case study can have.</span>
              <ReviewSearch picked={meta.review}
                onPick={(r) => { setMeta((m) => ({ ...m, review: r })); setSaved(false); }}
                onClear={() => { setMeta((m) => ({ ...m, review: undefined })); setSaved(false); }} />
            </div>
          </>
        )}

        {/* ---------- article: knowledge ---------- */}
        {flowKey === 'article-knowledge' && i === 0 && (
          <div className="es-fields">
            <div><span className="es-label">What&rsquo;s the topic?</span><Input placeholder="e.g. Ice dams on north-facing roofs" value={name} onChange={(e) => { setName(e.target.value); setSaved(false); }} /><span className="es-hint">A question customers ask you all the time works best.</span></div>
            <div><span className="es-label">Who is this for?</span>
              {/* Free text with suggestions, not a fixed list: the audience goes
                  into the interview verbatim, and a commercial roofer, a B2B
                  supplier or a clinic is not writing for "homeowners". */}
              <Input list="es-audience" placeholder="e.g. Homeowners comparing options, facility managers, general contractors…"
                value={fields.audience || ''} onChange={(e) => setField('audience', e.target.value)} autoComplete="off" />
              <datalist id="es-audience">
                {AUDIENCE_SUGGESTIONS.map((a) => <option key={a} value={a} />)}
              </datalist>
              <span className="es-hint">Pick a suggestion or describe your own reader.</span>
            </div>
            <div><span className="es-label">Anything we should be sure to cover?</span><Textarea rows={3} placeholder="Optional — a few notes on what matters most" value={fields.notes || ''} onChange={(e) => setField('notes', e.target.value)} /></div>
          </div>
        )}

        {/* ---------- article: blog details + draft ---------- */}
        {flowKey === 'article-blog' && i === 0 && (
          <div className="es-fields">
            <div><span className="es-label">Working title</span><Input placeholder="e.g. Repair or replace: how to decide" value={name} onChange={(e) => { setName(e.target.value); setSaved(false); }} /></div>
            <div><span className="es-label">Target keyword or topic</span><Input placeholder="Optional" value={fields.keyword || ''} onChange={(e) => setField('keyword', e.target.value)} /></div>
            <div><span className="es-label">Notes for our writers</span><Textarea rows={3} placeholder="Optional — anything we should know" value={fields.notes || ''} onChange={(e) => setField('notes', e.target.value)} /></div>
          </div>
        )}
        {flowKey === 'article-blog' && i === 1 && (
          <>
            <div className="es-section-note">
              <Icon name="sparkles" size={16} />
              <span>Paste what you have — rough is fine. We&rsquo;ll structure it, tighten the writing and keep your voice intact.</span>
            </div>
            <div className="es-fields">
              <div><span className="es-label">Your draft</span><Textarea rows={10} placeholder="Paste your article here…" value={meta.draftText || ''} onChange={(e) => { setMeta((m) => ({ ...m, draftText: e.target.value })); setSaved(false); }} /></div>
            </div>
            {project.articleId && (
              <div style={{ marginTop: 18 }}>
                <span className="es-label">Or upload a document</span>
                <ArticleDocDrop articleId={project.articleId} hint="We’ll pull the text out for you" />
              </div>
            )}
          </>
        )}

        {/* ---------- article: assets ---------- */}
        {project.type === 'article' && ((project.kind === 'blog' && i === 2) || (project.kind !== 'blog' && i === 1)) && project.articleId && (
          <>
            <div className="es-section-note">
              <Icon name="image" size={16} />
              <span>{project.kind === 'blog'
                ? 'Photos make an article far more credible. Attach job-site images, or pull in a gallery you’ve already built.'
                : 'Add your photos now — our AI reads them during the interview so the writing matches the actual work.'}</span>
            </div>
            {/* "Link an existing gallery" used to sit here. It never did
                anything on a case study — linked_gallery_id is only read on
                submit for type === 'social' — and it listed every legacy
                gallery the client had, so abandoned social scratch galleries
                turned up as options on a case study. */}
            {/* A case study's photos and the gallery are the same photos now.
                What stood here uploaded every file twice — once into a draft
                gallery it silently created and named after the case study, once
                into the article — leaving two copies that drifted apart from
                the moment they existed. One photo, one file, and the client
                chooses which album it also belongs to. */}
            {/* One component for every article kind. The knowledge and blog
                flows used a bare drop zone that kept its thumbnails in local
                state, so leaving the step and coming back showed an empty
                zone over photos that were in fact uploaded — read by a client
                as "it kept deleting the things I uploaded". */}
            <CaseStudyPhotos articleId={project.articleId} heading={project.kind === 'case' ? 'Photos from this project' : 'Images for this article'} />
            <div className="es-subhead"><span className="k">Documents</span><span className="n">Optional</span></div>
            <span className="es-hint" style={{ marginBottom: 12 }}>Inspection reports, estimates, insurance paperwork, spec sheets — our AI reads these for detail and they never publish publicly.</span>
            <ArticleDocDrop articleId={project.articleId} />
          </>
        )}

        {/* ---------- article: AI interview ---------- */}
        {project.type === 'article' && project.kind !== 'blog' && i === 2 && (
          <>
            <div className="es-section-note">
              <Icon name="sparkles" size={16} />
              <span>Answer in your own words — plain and specific is perfect. Our AI is listening for the details only someone who does this work would know, and our writers turn your answers into the finished piece.</span>
            </div>
            {interviewDone ? (
              <div className="es-checks" style={{ maxWidth: 620 }}>
                <div className="es-check"><Icon name="check-circle" size={16} /> Interview complete — your answers are saved to this project. You can continue to review, or run it again to add more.</div>
                <div style={{ marginTop: 10 }}>
                  <Button variant="outline" iconLeft={<Icon name="sparkles" size={15} />} onClick={() => setInterviewOpen(true)}>Reopen the interview</Button>
                </div>
              </div>
            ) : (
              <Button variant="primary" iconLeft={<Icon name="sparkles" size={16} />} onClick={() => { save.mutate({}); setInterviewOpen(true); }}>
                Start the AI interview
              </Button>
            )}
          </>
        )}

        {/* ---------- social ---------- */}
        {project.type === 'social' && i === 0 && (
          <>
            {/* Same screen as a case study's photos, over the same asset
                store. What stood here created a whole legacy gallery the moment
                you clicked the drop zone — before choosing a file — so every
                abandoned post left an empty "New social post photos" behind, in
                the client's gallery list and in every picker. */}
            <ProjectPhotos projectId={project.id} />
            <div style={{ marginTop: 18, maxWidth: 620 }}>
              <span className="es-label">Tell us about the job <span style={{ color: 'var(--text-subtle)', fontWeight: 400 }}>(a sentence is plenty)</span></span>
              <Textarea rows={3} placeholder="e.g. Full tear-off in Edina after hail. Insurance covered it — four days start to finish." value={fields.description || ''} onChange={(e) => setField('description', e.target.value)} />
            </div>
          </>
        )}
        {project.type === 'social' && i === 1 && (
          <>
            <div className="es-section-note">
              <Icon name="sparkles" size={16} />
              <span>Our AI scans your images and writes one caption per platform, tuned to how each platform reads. Edit any of them — each platform&rsquo;s feed carries its own version.</span>
            </div>
            {/* Saved with the project, so Regenerate keeps the choice and the
                admin queue can see what the client asked for. */}
            <div style={{ maxWidth: 420, marginBottom: 14 }}>
              <Select label="Tone & style" value={fields.tone || DEFAULT_SOCIAL_TONE}
                hint={SOCIAL_TONES.find((t) => t.id === (fields.tone || DEFAULT_SOCIAL_TONE))?.description}
                onChange={(e) => setField('tone', e.target.value)}
                options={SOCIAL_TONES.map((t) => ({ value: t.id, label: t.label }))} />
            </div>
            {!hasCaptions && (
              <Button variant="primary" iconLeft={<Icon name="sparkles" size={16} />}
                disabled={captions.isPending || (socialPhotoCount === 0 && !(fields.description || '').trim())}
                onClick={() => captions.mutate()}>
                {captions.isPending ? 'Reading your photos…' : 'Generate captions'}
              </Button>
            )}
            {(meta.vision?.length ?? 0) > 0 && (
              <div style={{ marginBottom: 18, marginTop: hasCaptions ? 0 : 18 }}>
                <span className="es-label">What the AI found in your photos</span>
                <div className="es-vision">
                  {meta.vision!.map((v) => <span className="es-vchip" key={v}><Icon name="check" size={10} /> {v}</span>)}
                </div>
              </div>
            )}
            {hasCaptions && (
              <>
                <div className="es-caps">
                  {SOCIAL_PLATFORMS.map((plat) => (
                    <div className="es-cap" key={plat}>
                      <div className="es-cap__top">
                        <Icon name="megaphone" size={14} />
                        <strong>{plat}</strong>
                      </div>
                      <Textarea rows={3} value={meta.captions?.[plat] || ''} placeholder={`Caption for ${plat}…`}
                        onChange={(e) => setPlatformCaption(plat, e.target.value)} />
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 10 }}>
                  <Button variant="ghost" size="sm" disabled={captions.isPending} onClick={() => captions.mutate()}>{captions.isPending ? 'Regenerating…' : 'Regenerate all'}</Button>
                </div>
              </>
            )}
          </>
        )}
        {project.type === 'social' && i === 2 && (
          <>
            <div className="es-section-note">
              <Icon name="send" size={16} />
              <span>Each platform has its own feed URL — once Triton approves this post, it appears only in the feeds you pick here, carrying that platform&rsquo;s caption.</span>
            </div>
            <span className="es-label">Post to</span>
            <div className="es-plats">
              {['Facebook', 'Instagram', 'LinkedIn'].map((p) => (
                <button type="button" className={`es-plat${(meta.platforms || []).includes(p) ? ' is-on' : ''}`} key={p} onClick={() => togglePlat(p)}>
                  <Icon name="megaphone" size={16} /> {p}
                </button>
              ))}
            </div>
            <div className="es-fields" style={{ marginTop: 22 }}>
              <div><span className="es-label">When should it post?</span>
                <Select placeholder="Select timing" options={['As soon as it’s approved', 'Hold in feed — I’ll decide later']}
                  value={fields.timing || ''} onChange={(e) => setField('timing', e.target.value)} />
              </div>
            </div>
          </>
        )}

        {/* ---------- review (always last) ---------- */}
        {last && (
          <div className="es-summary">
            <div>
              {project.type === 'social' ? (
                <div className="es-preview">
                  <div className="es-preview__bar"><Icon name="eye" size={13} /> Preview — how this could look on each platform</div>
                  <div className="es-preview__body es-preview__body--social">
                    {(meta.platforms?.length ? meta.platforms : ['Facebook']).map((plat) => (
                      <SocialPreview key={plat} platform={plat} bizName={bizName}
                        caption={meta.captions?.[plat] || meta.caption || ''}
                        image={previewImgNode} />
                    ))}
                  </div>
                  <div className="es-preview__edit">
                    <Button variant="outline" size="sm" iconLeft={<Icon name="edit" size={13} />} onClick={() => goTo(1)}>Edit captions</Button>
                    <Button variant="ghost" size="sm" iconLeft={<Icon name="image" size={13} />} onClick={() => goTo(0)}>Change images</Button>
                  </div>
                </div>
              ) : (
                <div className="es-preview">
                  <div className="es-preview__bar"><Icon name="eye" size={13} /> Preview — how this will publish</div>
                  <div className="es-preview__body">
                    <h4>{name || 'Untitled project'}</h4>
                    <p>{previewBody}</p>
                    <p style={{ color: 'var(--text-subtle)', fontStyle: 'italic', margin: 0 }}>Full draft continues — our writers polish before anything goes live.</p>
                  </div>
                </div>
              )}
            </div>
            <div>
              <span className="es-label">Ready to go</span>
              <div className="es-checks">
                {checks.map((c) => (
                  <div className="es-check" key={c.label} style={c.ok ? undefined : { opacity: 0.55 }}>
                    <Icon name={c.ok ? 'check-circle' : 'alert-circle'} size={16} /> {c.label}
                  </div>
                ))}
              </div>
              <span className="es-label" style={{ marginTop: 22 }}>What happens next?</span>
              <div className="es-route">
                {(
                  <>
                    <div className="es-routeopt is-on">
                      <span className="es-routeopt__ic"><Icon name="check" size={12} /></span>
                      <span>
                        <strong>Send to Triton for review</strong>
                        <span>Our team polishes and publishes it for you.</span>
                      </span>
                    </div>
                    <div className="es-routeopt is-off">
                      <span className="es-routeopt__ic" />
                      <span>
                        <strong>Publish it myself</strong>
                        <span>Coming soon — for now everything routes through Triton.</span>
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="es-flow__foot">
        {i > 0 && <Button variant="outline" iconLeft={<Icon name="arrow-left" size={16} />} onClick={() => goTo(i - 1)}>Back</Button>}
        <div className="right">
          <Button variant="ghost" onClick={saveAndClose}>Save &amp; close</Button>
          {last ? (
            <Button variant="primary" iconRight={<Icon name="send" size={17} />} disabled={submit.isPending} onClick={() => submit.mutate()}>
              {submit.isPending ? 'Sending…' : 'Send to Triton'}
            </Button>
          ) : (
            <Button variant="primary" iconRight={<Icon name="arrow-right" size={17} />} onClick={() => goTo(i + 1)}>Continue</Button>
          )}
        </div>
      </div>
    </div>
  );
}
